import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getBulkChatHistory, getMonitorContacts } from "../services/whatsapp";

// Admin-only, view-only screen: are sales and support replying to customers on WhatsApp?
const TABS = [
    { key: "b2c", label: "B2C sales · this month", person: "Salesperson", scope: "Customers with a B2C order this month (any status) who have a WhatsApp chat", link: (r) => `/orders/${r.ref_id}`, open: "Open order" },
    { key: "b2b", label: "B2B sales · last 90 days", person: "Salesperson", scope: "Customers with a B2B order in the last 90 days (any status) who have a WhatsApp chat", link: (r) => `/orders/${r.ref_id}`, open: "Open order" },
    { key: "support", label: "Support · last 50 tickets", person: "Support agent", scope: "Latest 50 support tickets whose customer has a WhatsApp chat", link: () => "/support-tickets", open: "Open tickets" },
];
const PAGE = 20; // numbers per chat-history call
const REFRESH_MS = 60 * 1000;

// Images, stickers, audio, video and files come as an S3 link in media_file.
const Media = ({ m, onLoad }) => {
    const url = m.media_file;
    if (!url) return null;
    const ext = url.split("?")[0].split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext) || m.media_type === "image" || m.media_type === "sticker") {
        return (
            <a href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt={m.file_name || m.media_type} onLoad={onLoad} className={`${m.media_type === "sticker" ? "h-28 w-28" : "max-h-64"} rounded-md object-contain`} />
            </a>
        );
    }
    if (["mp3", "ogg", "opus", "m4a", "aac", "amr", "wav"].includes(ext) || m.media_type === "audio") return <audio controls src={url} className="max-w-full" />;
    if (["mp4", "3gp", "mov", "webm"].includes(ext) || m.media_type === "video") return <video controls src={url} className="max-h-64 rounded-md" />;
    return <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-green-700 underline">{m.file_name || "Open file"}</a>;
};

// "📷 [Image Received]" style captions add nothing once the media is shown
const isPlaceholder = (t) => /^\S*\s*\[[A-Za-z ]+ (Received|Sent)\]$/.test((t || "").trim());

// Who wrote a message: the customer, the bot flow (has a node_id), a campaign, or our staff.
const sender = (m) => (!m.sent ? "customer" : m.raw_payload?.node_id ? "bot" : m.promotional ? "campaign" : "staff");

const ago = (ms) => {
    const min = Math.max(0, Math.round(ms / 60000));
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    return h < 24 ? `${h}h ${min % 60}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
};

const timeLabel = (iso) => {
    const d = new Date(iso);
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
        ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// Status of one chat. Bot answers don't count as a team reply.
const summarise = (messages) => {
    let lastCustomer = -1;
    messages.forEach((m, i) => { if (sender(m) === "customer") lastCustomer = i; });
    const last = messages[messages.length - 1];
    let status = "none";
    let waitingMs = 0;
    if (lastCustomer >= 0) {
        const replied = messages.slice(lastCustomer + 1).some((m) => sender(m) === "staff");
        status = replied ? "replied" : "waiting";
        if (!replied) waitingMs = Date.now() - new Date(messages[lastCustomer].created_at).getTime();
    }
    // First team reply time: first customer message → first staff message after it
    let firstReplyMs = null;
    const firstCustomer = messages.findIndex((m) => sender(m) === "customer");
    if (firstCustomer >= 0) {
        const reply = messages.slice(firstCustomer + 1).find((m) => sender(m) === "staff");
        if (reply) firstReplyMs = new Date(reply.created_at) - new Date(messages[firstCustomer].created_at);
    }
    return { status, waitingMs, firstReplyMs, last };
};

const TAG = {
    waiting: "bg-amber-100 text-amber-800",
    replied: "bg-green-100 text-green-800",
    none: "bg-gray-100 text-gray-700",
};

const pill = (on) =>
    `inline-flex items-center gap-2 rounded-full border px-3.5 text-sm font-bold ${on ? "border-green-700 bg-green-700 text-white" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`;

const WhatsappMonitor = () => {
    const [tab, setTab] = useState("b2c");
    const [data, setData] = useState({});
    const dataRef = useRef({});
    useEffect(() => { dataRef.current = data; }, [data]);
    const [loading, setLoading] = useState(false);
    const [checkedAt, setCheckedAt] = useState(null);
    const [person, setPerson] = useState("");
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [, tick] = useState(0);

    // data[tab] = { contacts: all numbers from our backend, next: how many sent to the chat API, chats: {phone: messages} }
    const busy = useRef(false);

    const fetchChats = async (phones) => getBulkChatHistory(phones, 20, PAGE);

    // Next 20 numbers of a tab → their chats
    const loadMore = useCallback(async (key) => {
        const st = dataRef.current[key];
        if (!st || busy.current || st.next >= st.contacts.length) return;
        busy.current = true;
        setLoading(true);
        try {
            const phones = st.contacts.slice(st.next, st.next + PAGE).map((c) => c.phone);
            const chats = await fetchChats(phones);
            setData((d) => ({ ...d, [key]: { ...d[key], next: st.next + PAGE, chats: { ...d[key].chats, ...chats } } }));
            setCheckedAt(Date.now());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load WhatsApp chats");
        } finally {
            busy.current = false;
            setLoading(false);
        }
    }, []);

    // First open of a tab: the number list, then the first 20 chats
    useEffect(() => {
        if (dataRef.current[tab]) return;
        (async () => {
            try {
                const { data: res } = await getMonitorContacts(tab);
                dataRef.current = { ...dataRef.current, [tab]: { contacts: res.contacts, next: 0, chats: {} } };
                setData(dataRef.current);
                loadMore(tab);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not load contacts");
            }
        })();
    }, [tab, loadMore]);

    // Refresh the chats already loaded in the open tab every minute (20 numbers per call)
    useEffect(() => {
        const id = setInterval(async () => {
            const st = dataRef.current[tab];
            if (!st || busy.current || !st.next) return;
            try {
                const chats = await fetchChats(st.contacts.slice(0, st.next).map((c) => c.phone));
                setData((d) => ({ ...d, [tab]: { ...d[tab], chats: { ...d[tab].chats, ...chats } } }));
                setCheckedAt(Date.now());
            } catch (e) { /* try again next minute */ }
        }, REFRESH_MS);
        return () => clearInterval(id);
    }, [tab]);

    // Keep "checked X ago" and waiting times moving
    useEffect(() => {
        const id = setInterval(() => tick((n) => n + 1), 15000);
        return () => clearInterval(id);
    }, []);

    // Only numbers that have a chat; latest message first
    const tabData = data[tab];
    const rows = useMemo(() => {
        if (!tabData) return [];
        return tabData.contacts
            .filter((c) => (tabData.chats[c.phone] || []).length)
            // backend "status" is the order/ticket status; ours is the reply status
            .map((c) => ({ ...c, stage: c.status, messages: tabData.chats[c.phone], ...summarise(tabData.chats[c.phone]) }))
            .sort((a, b) => new Date(b.last.created_at) - new Date(a.last.created_at));
    }, [tabData]);
    const hasMore = !!tabData && tabData.next < tabData.contacts.length;

    // Load the next 20 when the bottom of the list scrolls into view
    const listRef = useRef(null);
    const endRef = useRef(null);
    useEffect(() => {
        if (!endRef.current || !hasMore) return;
        const obs = new IntersectionObserver(([e]) => e.isIntersecting && loadMore(tab), { root: listRef.current });
        obs.observe(endRef.current);
        return () => obs.disconnect();
    }, [tab, hasMore, loadMore, tabData]);
    const tabInfo = TABS.find((t) => t.key === tab);

    const people = useMemo(() => {
        const map = new Map();
        rows.forEach((r) => r.handled_by && map.set(String(r.handled_by.id), r.handled_by.name));
        return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [rows]);

    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((r) =>
            (!person || String(r.handled_by?.id) === person) &&
            (filter === "all" || r.status === filter) &&
            (!q || (r.name || "").toLowerCase().includes(q) || r.phone.includes(q)));
    }, [rows, person, filter, search]);

    const stats = useMemo(() => {
        const replies = shown.map((r) => r.firstReplyMs).filter((v) => v != null);
        return {
            chats: shown.length,
            replied: shown.filter((r) => r.status === "replied").length,
            waiting: shown.filter((r) => r.status === "waiting").length,
            avg: replies.length ? ago(replies.reduce((a, b) => a + b, 0) / replies.length) : "—",
        };
    }, [shown]);

    const sel = shown.find((r) => r.phone === selected) || shown[0];

    // Open each chat at its newest message
    const chatRef = useRef(null);
    const selPhone = sel?.phone;
    const selCount = sel?.messages.length;
    const toBottom = () => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; };
    useEffect(toBottom, [selPhone, selCount]);

    const pickTab = (key) => { setTab(key); setPerson(""); setFilter("all"); setSelected(null); };

    return (
        <div className="flex flex-col gap-4 p-4 md:p-5">
            <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white px-5 py-4">
                <div>
                    <h1 className="text-xl font-bold text-gray-900">WhatsApp Monitor</h1>
                    <p className="mt-1 text-sm text-gray-600">Check that sales and support reply to customers on time. View only.</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
                    {checkedAt ? `Live · checked ${ago(Date.now() - checkedAt)} ago` : "Loading…"}
                </div>
            </section>

            <nav aria-label="Team" className="flex flex-wrap gap-2">
                {TABS.map((t) => {
                    const waiting = t.key === tab ? rows.filter((r) => r.status === "waiting").length : null;
                    return (
                        <button key={t.key} type="button" aria-pressed={tab === t.key} onClick={() => pickTab(t.key)} className={`h-10 ${pill(tab === t.key)}`}>
                            {t.label}
                            {waiting != null && <span className={`rounded-full px-2 py-0.5 text-xs font-extrabold ${tab === t.key ? "bg-white" : "bg-amber-100"} text-amber-800`}>{waiting} waiting</span>}
                        </button>
                    );
                })}
            </nav>

            <section aria-label="Summary" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {[["Chats", stats.chats, "text-gray-900"], ["Replied", stats.replied, "text-green-800"], ["Customer waiting", stats.waiting, "text-amber-700"], ["Avg first reply", stats.avg, "text-gray-900"]].map(([label, value, color]) => (
                    <div key={label} className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-gray-600">{label}</div>
                        <div className={`mt-1 text-2xl font-extrabold ${color}`}>{value}</div>
                    </div>
                ))}
            </section>

            <section className="flex flex-wrap items-stretch gap-4">
                <aside className="flex min-w-0 flex-[1_1_340px] flex-col overflow-hidden rounded-xl border border-gray-100 bg-white lg:max-w-[440px]">
                    <div className="flex flex-col gap-2 border-b border-gray-100 p-3">
                        <div className="flex flex-wrap gap-2">
                            <label className="flex flex-[1_1_140px] flex-col gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-600">
                                {tabInfo.person}
                                <select value={person} onChange={(e) => setPerson(e.target.value)} className="h-[38px] rounded-lg border border-gray-300 bg-white px-2.5 text-sm normal-case tracking-normal text-gray-700">
                                    <option value="">All</option>
                                    {people.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                                </select>
                            </label>
                            <label className="flex flex-[1_1_140px] flex-col gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-600">
                                Search
                                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or number" className="h-[38px] rounded-lg border border-gray-300 px-2.5 text-sm normal-case tracking-normal" />
                            </label>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {[["all", "All"], ["waiting", "Customer waiting"], ["replied", "Replied"], ["none", "No reply from customer"]].map(([k, label]) => (
                                <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)} className={`h-8 text-xs ${pill(filter === k)}`}>{label}</button>
                            ))}
                        </div>
                        <div className="text-xs text-gray-600">{tabInfo.scope}</div>
                    </div>
                    <ul ref={listRef} className="max-h-[560px] overflow-y-auto">
                        {loading && !rows.length && <li className="p-4 text-sm text-gray-600">Loading chats…</li>}
                        {!loading && !hasMore && !shown.length && <li className="p-4 text-sm text-gray-600">No chats here.</li>}
                        {shown.map((r) => (
                            <li key={r.phone}>
                                <button type="button" onClick={() => setSelected(r.phone)} className={`flex w-full flex-col gap-0.5 border-b border-gray-100 p-3 text-left ${sel?.phone === r.phone ? "bg-green-50" : "bg-white hover:bg-gray-50"}`}>
                                    <span className="flex justify-between gap-2">
                                        <span className="truncate text-sm font-bold text-gray-900">{r.name || `+${r.phone}`}</span>
                                        <span className="whitespace-nowrap text-[11px] text-gray-600">{timeLabel(r.last.created_at)}</span>
                                    </span>
                                    <span className="text-xs text-gray-600">{[r.subject, r.stage, r.handled_by?.name || "Not assigned"].filter(Boolean).join(" · ")}</span>
                                    <span className="truncate text-[13px] text-gray-700">{r.last.message_text || r.last.media_type}</span>
                                    <span className={`mt-0.5 self-start rounded-full px-2 py-0.5 text-[11px] font-extrabold ${TAG[r.status]}`}>
                                        {r.status === "waiting" ? `Customer waiting ${ago(r.waitingMs)}` : r.status === "replied" ? "Replied" : "No reply from customer"}
                                    </span>
                                </button>
                            </li>
                        ))}
                        {hasMore && <li ref={endRef} className="p-3 text-center text-xs text-gray-600">{loading ? "Loading more…" : "Scroll for more"}</li>}
                    </ul>
                    {tabData && <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-600">Checked {Math.min(tabData.next, tabData.contacts.length)} of {tabData.contacts.length} customers · sorted by latest message</div>}
                </aside>

                <article className="flex min-w-0 flex-[999_1_560px] flex-col overflow-hidden rounded-xl border border-gray-100 bg-white">
                    {sel ? (
                        <>
                            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
                                <div>
                                    <div className="text-base font-extrabold text-gray-900">{sel.name || `+${sel.phone}`}</div>
                                    <div className="text-xs text-gray-600">+{sel.phone}{sel.stage ? ` · ${sel.stage}` : ""} · Handled by {sel.handled_by?.name || "nobody"}</div>
                                </div>
                                <Link to={tabInfo.link(sel)} className="inline-flex h-9 items-center rounded-lg border border-gray-300 px-3.5 text-sm font-bold text-green-700 hover:bg-gray-50">{tabInfo.open}</Link>
                            </div>
                            <div ref={chatRef} className="flex max-h-[560px] min-h-[420px] flex-1 flex-col gap-2 overflow-y-auto bg-[#efeae2] p-4">
                                {sel.messages.map((m) => {
                                    const who = sender(m);
                                    return (
                                        <div key={m.id} className={`max-w-[70%] rounded-lg px-3 py-2 ${who === "customer" ? "self-start bg-white" : who === "staff" ? "self-end bg-[#d9fdd3]" : "self-end bg-blue-50"}`}>
                                            <div className="text-[11px] font-bold text-gray-600">{{ customer: "Customer", bot: "Bot", campaign: "Campaign", staff: "Staff" }[who]}</div>
                                            <Media m={m} onLoad={toBottom} />
                                            {!(m.media_file && isPlaceholder(m.message_text)) && <div className="whitespace-pre-line break-words text-sm text-gray-900">{m.message_text || `[${m.media_type}]`}</div>}
                                            <div className="text-right text-[11px] text-gray-600">{timeLabel(m.created_at)}</div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="border-t border-gray-100 px-4 py-2.5 text-xs text-gray-600">View only · last 20 messages</div>
                        </>
                    ) : (
                        <div className="p-6 text-sm text-gray-600">Pick a chat to read it.</div>
                    )}
                </article>
            </section>
        </div>
    );
};

export default WhatsappMonitor;
